/**
 * Estimator — estimation temps/VRAM (debounce) + preflight, centralisés.
 * Les workspaces demandent une estimation à chaque changement de paramètre ;
 * le service débounce et notifie le dernier abonné.
 */
import { APIClient } from "../api/client.js";
import type {
	OptimizationOptions,
	PreflightResponse,
	TimeEstimationResponse,
} from "../types/index.js";
import { debounce } from "../utils/debounce.js";

export interface EstimateInput {
	width: number;
	height: number;
	steps: number;
	scheduler?: string;
	optimizationOptions?: OptimizationOptions;
	architecture?: string;
	hasControlnet?: boolean;
}

export interface PreflightInput {
	modelId: string;
	width: number;
	height: number;
	steps: number;
	batchSize?: number;
	optimizationOptions?: OptimizationOptions;
	architecture?: string;
}

export class Estimator {
	private onEstimate: ((result: TimeEstimationResponse | null) => void) | null = null;
	private onPreflight: ((result: PreflightResponse | null) => void) | null = null;

	private readonly debouncedEstimate = debounce(async (input: EstimateInput) => {
		try {
			const result = await APIClient.estimateTime(
				input.width,
				input.height,
				input.steps,
				input.scheduler,
				input.optimizationOptions,
				input.architecture,
				input.hasControlnet
			);
			this.onEstimate?.(result);
		} catch {
			this.onEstimate?.(null);
		}
	}, 500);

	private readonly debouncedPreflight = debounce(async (input: PreflightInput) => {
		try {
			const result = await APIClient.preflight(
				input.modelId,
				input.width,
				input.height,
				input.steps,
				input.batchSize ?? 1,
				input.optimizationOptions,
				input.architecture
			);
			this.onPreflight?.(result);
		} catch {
			this.onPreflight?.(null);
		}
	}, 500);

	onEstimateUpdate(listener: (result: TimeEstimationResponse | null) => void): void {
		this.onEstimate = listener;
	}

	onPreflightUpdate(listener: (result: PreflightResponse | null) => void): void {
		this.onPreflight = listener;
	}

	requestEstimate(input: EstimateInput): void {
		void this.debouncedEstimate(input);
	}

	requestPreflight(input: PreflightInput): void {
		void this.debouncedPreflight(input);
	}
}
